/**
 * sales-order-api — 판매 현황(실데이터)의 API(백엔드 V장 "판매 공사", 2026-11-02).
 *
 * 백엔드 `modules/sales-order/SalesOrder.Types.ts`(hcltech-parts)를 Zod 로 비춘다. 판매(sales_order) 한 건 = 한 줄 · 번호는 견적 번호.
 * 열거는 백엔드 철자 그대로 — 백엔드 CHECK 를 바꾸면 여기도 같이 고친다.
 */
import { z } from 'zod';
import { E_STOCK_STATUSES } from './inventory-api';
import { getJson, postJson, putJson } from './http';

const BASE = '/api/admin/sales-orders';

export const SALES_ORDER_LIMIT = 500;   // 백엔드 최대와 같다 — 한 쪽

import { E_SALES_ORDER_STATUSES } from './sales-order-status';
export { E_SALES_ORDER_STATUSES } from './sales-order-status';   // 판매 상태 여섯 — 실물 줄과 같이 쓰려고 옮김(2026-11-04)
export type { ESalesOrderStatus } from './sales-order-status';

/** 취소 사유 둘 — 백엔드 E_CLOSED_REASONS(cancelled · closed 둘 다 쓴다). 감액은 2026-11-03 뺌 — 판매 상세의 단가 고치기로 */
export const E_CLOSED_REASONS = ['customer_cancelled', 'etc'] as const;
export type  EClosedReason    = typeof E_CLOSED_REASONS[number];

/** 목록 탭 — open = 진행 중(판매 등록 · 출고 요청 · 세팅 완료, 기본) · 상태 하나 · all */
export const E_SALES_ORDER_FILTERS = ['open', ...E_SALES_ORDER_STATUSES, 'all'] as const;
export type  ESalesOrderFilter     = typeof E_SALES_ORDER_FILTERS[number];

/** 목록 한 줄에 미리 보이는 품목 — 앞 3줄. 품목마다 판매 · 원가 · 마진도 쌓아 보인다(2026-11-02 목록 꼴 다시) */
export const SalesOrderItemHeadSchema = z.strictObject({
  option_part:   z.string().nullable(),
  description:   z.string().nullable(),
  condition:     z.string().nullable(),
  quantity:      z.number().int(),
  sale_amount:   z.number().nullable(),   // 단가 × 수량, 단가 없으면 null
  cost_amount:   z.number().nullable(),   // 다 묶이고 다 원가가 있을 때만
  margin_amount: z.number().nullable(),   // 둘 중 하나 null 이면 null
});

/** 판매 한 건 = 목록 한 줄. 번호는 견적 번호 */
export const SalesOrderViewSchema = z.strictObject({
  sales_order_id:       z.string(),
  quote_id:             z.string(),
  quote_no:             z.string(),
  deal_id:              z.string().nullable(),          // 이어진 딜 — 견적 없는 판매는 [딜 연결] 전까지 null(백엔드 364)
  deal_no:              z.string().nullable(),          // 그 딜의 번호 — 딜 링크 글
  customer_name:        z.string().nullable(),          // 판매의 거래처(판매가 쥔다 — 딜을 바꿔도 안 흔들린다)
  status:               z.enum(E_SALES_ORDER_STATUSES),
  closed_reason:        z.enum(E_CLOSED_REASONS).nullable(),
  closed_note:          z.string().nullable(),          // 취소 사유 글 — 기타면 늘 있다
  item_count:           z.number().int().nonnegative(),
  quantity_total:       z.number().int().nonnegative(),
  assigned_total:       z.number().int().nonnegative(),
  shortage_total:       z.number().int().nonnegative(),   // 미매칭 + 구매 필요
  purchase_needed_total: z.number().int().nonnegative(),  // 그중 가용을 다 모아도 모자란 수(로트 매칭 ③, 2026-11-05). 미매칭 = shortage_total − 이것
  requested_ship_date:  z.string().nullable(),          // 'YYYY-MM-DD'
  tax_invoice_date:     z.string().nullable(),          // 세금계산서 발행일 'YYYY-MM-DD' — null = 아직(백엔드 365 · 2026-11-03)
  created_at:           z.string(),                     // ISO
  manager_name:         z.string().nullable(),
  sale_amount:          z.number().nullable(),          // 견적 총액(공급가 KRW)
  cost_amount:          z.number().nullable(),          // 모든 행의 원가가 확정일 때만 합, 아니면 null
  margin_amount:        z.number().nullable(),          // 확정 마진 — 모든 행의 판매가 · 원가가 있을 때만
  cost_missing_count:   z.number().int().nonnegative(), // 원가가 확정 안 된 행 수 — 0 이면 확정
  items_head:           z.array(SalesOrderItemHeadSchema),
});

export const SalesOrderViewListSchema = z.strictObject({
  rows:   z.array(SalesOrderViewSchema),
  total:  z.number().int().nonnegative(),
  limit:  z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});

/** 로트 하나에서 몇 장 — 묶인 실물을 로트로 센 것이자 [로트 저장] 몸의 한 줄(백엔드 StockLineMatch). 로트 = 구매 줄(line_key) */
export const StockLineMatchSchema = z.strictObject({
  line_key: z.string(),
  quantity: z.number().int().positive(),
});

/** 품목 행에 묶인 실물 하나 */
export const AssignedUnitViewSchema = z.strictObject({
  unit_id:       z.string(),
  serial_number: z.string().nullable(),
  stock_status:  z.enum(E_STOCK_STATUSES),
});

/** 펼친 곳의 한 줄 = 견적 품목 행(부모 행만) */
export const SalesOrderItemViewSchema = z.strictObject({
  quote_item_id:     z.string(),
  hclt_product_id:   z.string(),     // 재등록 — 판매 등록 줄의 제품
  hclt_number:       z.string(),
  product_type:      z.string(),
  is_config:         z.boolean(),
  option_part:       z.string().nullable(),
  description:       z.string().nullable(),
  category:          z.string().nullable(),
  condition:         z.string().nullable(),
  quantity:          z.number().int(),
  assigned:          z.number().int().nonnegative(),
  shortage:          z.number().int().nonnegative(),   // 미매칭 + 구매 필요
  purchase_needed:   z.number().int().nonnegative(),   // 그중 가용을 다 모아도 모자란 수
  lines:             z.array(StockLineMatchSchema),    // 묶인 실물을 로트로 센 것
  units:             z.array(AssignedUnitViewSchema),
  unit_price_krw:    z.number().nullable(),
  sale_amount:       z.number().nullable(),
  cost_amount:       z.number().nullable(),
  margin_amount:     z.number().nullable(),
});

/** 판매 한 건의 상세 — 펼침의 재료 */
/** 판매 상세의 거래처 칸 — company 연락 칸(이름은 customer_name) */
export const SalesCustomerViewSchema = z.strictObject({
  business_no:          z.string().nullable(),
  representative_name:  z.string().nullable(),
  representative_phone: z.string().nullable(),
  representative_email: z.string().nullable(),
  address:              z.string().nullable(),
});

/** 판매 상세의 연락처 — 견적의 연락처(판매엔 칸이 없다) */
export const SalesContactViewSchema = z.strictObject({
  name:         z.string(),
  position:     z.string().nullable(),
  phone:        z.string().nullable(),
  office_phone: z.string().nullable(),
  email:        z.string().nullable(),
});

export const SalesOrderDetailSchema = SalesOrderViewSchema.extend({
  customer_id:      z.string().nullable(),   // 재등록 — 딜의 거래처
  assignee_user_id: z.string().nullable(),   // 재등록 — 딜의 담당자
  ship_to: z.string().nullable(),
  note:    z.string().nullable(),
  customer: SalesCustomerViewSchema.nullable(),   // 거래처 없는 판매면 null
  contact:  SalesContactViewSchema.nullable(),    // 견적에 연락처가 없으면 null
  items:   z.array(SalesOrderItemViewSchema),
});

/** 목록 질의 — 탭 · 찾기(견적 번호 · 고객 · 파트넘버, '' = 없음) */
export type SalesOrderListQuery = { filter: ESalesOrderFilter; q: string };

export type SalesOrderItemHead = z.infer<typeof SalesOrderItemHeadSchema>;
export type SalesOrderView = z.infer<typeof SalesOrderViewSchema>;
export type SalesOrderViewList = z.infer<typeof SalesOrderViewListSchema>;
export type AssignedUnitView = z.infer<typeof AssignedUnitViewSchema>;
export type SalesOrderItemView = z.infer<typeof SalesOrderItemViewSchema>;
export type SalesOrderDetail = z.infer<typeof SalesOrderDetailSchema>;
export type SalesCustomerView = z.infer<typeof SalesCustomerViewSchema>;
export type SalesContactView = z.infer<typeof SalesContactViewSchema>;

/** 견적 없는 판매 등록의 줄 하나 — 백엔드 POST /sales-orders 몸의 lines(id 는 숫자로 보낸다) */
export type DirectSaleLine = { hclt_product_id: number; condition: string; quantity: number; unit_price_krw: number };
/** 견적 없는 판매 등록 몸 — 백엔드 DirectSaleInput. 고객 담당자 칸은 이번엔 없어 늘 null(2026-11-02) */
export type DirectSaleInput = {
  customer_id: number;
  contact_id: null;
  assignee_user_id: number | null;   // null = 등록한 사람(서버가 채움)
  note: string | null;
  lines: DirectSaleLine[];
};

/** POST /sales-orders — 견적 없는 판매 등록. 만든 판매의 상세. 400 · 404 는 서버 글 그대로 throw(postJson) */
export async function createDirectSale(input: DirectSaleInput): Promise<SalesOrderDetail> {
  return postJson(BASE, input, SalesOrderDetailSchema);
}

/** 컨디션 하나의 가용 수 — 백엔드 ConditionAvailable */
export const ConditionAvailableSchema = z.strictObject({
  condition: z.string(),
  available: z.number().int().positive(),
});
/** 판매 등록 제품 찾기 한 줄 — 백엔드 DirectSaleProductMatch. 단품 + 보유 실물 있는 구성 */
export const DirectSaleProductMatchSchema = z.strictObject({
  hclt_product_id: z.string(),
  hclt_number: z.string(),
  product_type: z.string(),
  is_config: z.boolean(),                         // 구성 줄이 있음 — 칩 "세트" · "서버 구성"
  option_part: z.string().nullable(),
  description: z.string().nullable(),
  available: z.array(ConditionAvailableSchema),   // 가용 1 이상인 컨디션만
});
const DirectSaleProductMatchListSchema = z.strictObject({ rows: z.array(DirectSaleProductMatchSchema) });
export type ConditionAvailable     = z.infer<typeof ConditionAvailableSchema>;
export type DirectSaleProductMatch = z.infer<typeof DirectSaleProductMatchSchema>;

/** 판매 등록 제품 찾기 — GET /sales-orders/product-matches */
export async function fetchDirectSaleProductMatches(q: string, limit: number): Promise<DirectSaleProductMatch[]> {
  const params = new URLSearchParams({ q, limit: String(limit) });
  const list = await getJson(`${BASE}/product-matches?${params.toString()}`, { schema: DirectSaleProductMatchListSchema });
  return list.rows;
}

/** 목록 — 탭 · 찾기. 한 쪽(SALES_ORDER_LIMIT) */
export async function fetchSalesOrders(query: SalesOrderListQuery): Promise<SalesOrderViewList> {
  const params = new URLSearchParams({ filter: query.filter, limit: String(SALES_ORDER_LIMIT), offset: '0' });
  if (query.q !== '') params.set('q', query.q);
  return getJson(`${BASE}?${params.toString()}`, { schema: SalesOrderViewListSchema });
}

/** 판매 한 건 — 펼침의 재료 */
export async function fetchSalesOrder(id: string): Promise<SalesOrderDetail> {
  return getJson(`${BASE}/${encodeURIComponent(id)}`, { schema: SalesOrderDetailSchema });
}

/** [판매 확정] 판매 등록 → 판매 확정(2026-11-05). 부족이 남았거나 상태가 다르면 409 */
export async function confirmSalesOrder(id: string): Promise<SalesOrderDetail> {
  return postJson(`${BASE}/${encodeURIComponent(id)}/confirm`, {}, SalesOrderDetailSchema);
}

/** [확정 취소] 판매 확정 → 판매 등록. 상태가 다르면 409 */
export async function unconfirmSalesOrder(id: string): Promise<SalesOrderDetail> {
  return postJson(`${BASE}/${encodeURIComponent(id)}/unconfirm`, {}, SalesOrderDetailSchema);
}

/** [출고 요청 취소] 출고 요청 → 판매 확정. 상태가 다르면 409 */
export async function cancelSalesOrderDispatchRequest(id: string): Promise<SalesOrderDetail> {
  return postJson(`${BASE}/${encodeURIComponent(id)}/dispatch-request/cancel`, {}, SalesOrderDetailSchema);
}

/** [출고 요청] 판매 확정 → 출고 요청. 부족이 남았으면 409 */
export async function requestSalesOrderDispatch(id: string): Promise<SalesOrderDetail> {
  return postJson(`${BASE}/${encodeURIComponent(id)}/request-dispatch`, {}, SalesOrderDetailSchema);
}

/** [세팅 완료] 출고 요청 → 세팅 완료(확인만). 부족 · 상태가 다르면 409 */
export async function completeSalesOrderSetup(id: string): Promise<SalesOrderDetail> {
  return postJson(`${BASE}/${encodeURIComponent(id)}/setup-done`, {}, SalesOrderDetailSchema);
}

/** [출고] 출고 요청 · 세팅 완료 → 출고 완료(판매 통째). 임대 · 부족 · 상태가 다르면 409 */
export async function dispatchSalesOrder(id: string): Promise<SalesOrderDetail> {
  return postJson(`${BASE}/${encodeURIComponent(id)}/dispatch`, {}, SalesOrderDetailSchema);
}

/** 취소 모달이 보내는 것 — 백엔드 SalesOrderCancelInput */
export type SalesOrderCancelInput = { closed_reason: EClosedReason; closed_note: string | null };

/** [취소] → 판매 한 건 전체. 나간 실물이 있으면 서버가 closed(일부 출고)로. 기타인데 글이 없으면 400 */
export async function cancelSalesOrder(id: string, input: SalesOrderCancelInput): Promise<SalesOrderDetail> {
  return postJson(`${BASE}/${encodeURIComponent(id)}/cancel`, input, SalesOrderDetailSchema);
}

/** 판매 상세의 품목 고치기 — 컨디션 · 수량 · 단가 셋을 늘 같이(백엔드 SalesOrderItemEdit) */
export type SalesOrderItemEdit = { condition: string; quantity: number; unit_price_krw: number };

/** [저장] 의 고친 행 하나 — 어느 품목 행을 어떻게 */
export type SalesOrderItemEditLine = SalesOrderItemEdit & { quote_item_id: string };

/** [저장] 판매 상세의 품목 — 고친 행들 + 새 줄들 한 트랜잭션. 상태가 다르면 409 · 값 · 제품이 틀리면 400 */
export async function saveSalesOrderItems(id: string, edits: SalesOrderItemEditLine[], lines: DirectSaleLine[]): Promise<SalesOrderDetail> {
  return putJson(`${BASE}/${encodeURIComponent(id)}/items`, { edits, lines }, SalesOrderDetailSchema);
}

/** 맞춤 창의 고를 로트 한 줄 — 그 스탁의 입고된 구매 줄 중 가용 1 이상(백엔드 StockLineOption). 오래된 구매 순 */
export const StockLineOptionSchema = z.strictObject({
  line_key:       z.string(),
  source:         z.enum(['ecount', 'po', 'none']),
  purchased_date: z.string().nullable(),   // 'YYYY-MM-DD'
  supplier_name:  z.string().nullable(),
  purchase_no:    z.string().nullable(),
  unit_price:     z.number().nullable(),   // 재고 목록과 같은 단가
  available:      z.number().int().positive(),
});
/** 맞춤 창 재료 — 품목 행 하나(백엔드 SaleItemLineChoice). incoming = 구매 중(보이기만 — 엮지 않는다) */
export const SaleItemLineChoiceSchema = z.strictObject({
  quote_item_id: z.string(),
  matched:       z.array(StockLineMatchSchema),
  options:       z.array(StockLineOptionSchema),
  incoming:      z.number().int().nonnegative(),
});
const SaleItemLineChoiceListSchema = z.strictObject({ rows: z.array(SaleItemLineChoiceSchema) });
export type StockLineMatch     = z.infer<typeof StockLineMatchSchema>;
export type StockLineOption    = z.infer<typeof StockLineOptionSchema>;
export type SaleItemLineChoice = z.infer<typeof SaleItemLineChoiceSchema>;
/** [로트 저장] 몸의 한 행 — 보낸 것이 그 행의 맞춤 전체(빠진 로트는 풀린다) */
export type SaleItemLineMatchInput = { quote_item_id: string; matches: StockLineMatch[] };

/** GET /:id/line-choices — 로트 맞춤 창 재료(부모 품목 행마다) */
export async function fetchSaleItemLineChoices(id: string): Promise<SaleItemLineChoice[]> {
  const list = await getJson(`${BASE}/${encodeURIComponent(id)}/line-choices`, { schema: SaleItemLineChoiceListSchema });
  return list.rows;
}

/** [로트 저장] PUT /:id/lines — 고른 로트의 실물을 묶는다. 판매 등록만 · 로트 가용이 모자라면 409 · 합이 수량을 넘으면 400 */
export async function saveSaleItemLineMatches(id: string, items: SaleItemLineMatchInput[]): Promise<SalesOrderDetail> {
  return putJson(`${BASE}/${encodeURIComponent(id)}/lines`, { items }, SalesOrderDetailSchema);
}

/** 판매 이력 한 줄 — 딜 연결 · 품목 고치기 · 추가(백엔드 SalesEventView) */
export const SalesEventViewSchema = z.strictObject({
  id:         z.string(),
  message:    z.string(),
  actor_name: z.string().nullable(),
  created_at: z.string(),   // ISO
});
export type SalesEventView = z.infer<typeof SalesEventViewSchema>;
const SalesEventListSchema = z.strictObject({ events: z.array(SalesEventViewSchema) });

/** GET /:id/events — 판매 이력(최신순) */
export async function fetchSalesOrderEvents(id: string): Promise<SalesEventView[]> {
  const list = await getJson(`${BASE}/${encodeURIComponent(id)}/events`, { schema: SalesEventListSchema });
  return list.events;
}

/** 세금계산서 발행일(백엔드 365) — null 이면 비움. 없는 날 400 · 취소(cancelled)된 판매 409 */
export async function updateSalesOrderTaxInvoice(id: string, taxInvoiceDate: string | null): Promise<SalesOrderDetail> {
  return putJson(`${BASE}/${encodeURIComponent(id)}/tax-invoice`, { tax_invoice_date: taxInvoiceDate }, SalesOrderDetailSchema);
}

/** [딜 연결] 딜 없는 판매를 기존 딜에(백엔드 364). 이미 이어짐 · 다른 거래처의 딜 · 채택 견적 있는 딜이면 409 */
export async function linkSalesOrderDeal(id: string, dealId: number): Promise<SalesOrderDetail> {
  return postJson(`${BASE}/${encodeURIComponent(id)}/deal`, { deal_id: dealId }, SalesOrderDetailSchema);
}
